import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Info, Loader2, ArrowUp, ArrowDown, Filter, ChevronDown } from 'lucide-react';
import { AccountFinal } from '@/api/entities';
import { useTranslation } from '@/components/utils/translations';
import { createPageUrl } from '@/utils';
import { sanitizeId } from '@/components/utils/id';
import CountryActivityHeatmapCard from '@/components/analytics/CountryActivityHeatmapCard';
import { appendAnalyticsWindow, coerceAnalyticsWindow } from '@/lib/analyticsWindow';

const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

function formatRate(rate, sends) {
  const r = Number(rate);
  const s = Number(sends || 0);
  if (!Number.isFinite(r) || s <= 0) return '—';
  return `${r.toFixed(1)}%`;
}

/** Collapse duplicate institution rows (same name, different account_id keys from analytics). */
function normalizeAccountNameKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function getAccountFinalPageUrl(accountId) {
  const id = sanitizeId(accountId);
  if (!id) return null;
  return createPageUrl(`AccountFinal?id=${encodeURIComponent(id)}`);
}

/** Display account fit as a whole number (0–1 scale → 0–100). */
function formatAccountFitInteger(score) {
  if (score == null || score === '') return '—';
  const n = Number(score);
  if (!Number.isFinite(n)) return '—';
  const v = n > 0 && n <= 1 ? Math.round(n * 100) : Math.round(n);
  return String(v);
}

function mergeTopAccountsRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const byName = new Map();
  const withoutName = [];

  const mergeFit = (a, b) => {
    const na = a == null || a === '' ? null : Number(a);
    const nb = b == null || b === '' ? null : Number(b);
    if (na != null && Number.isFinite(na) && nb != null && Number.isFinite(nb) && na !== nb) {
      return Math.max(na, nb);
    }
    return Number.isFinite(na) ? na : Number.isFinite(nb) ? nb : null;
  };

  for (const row of list) {
    const nameKey = normalizeAccountNameKey(row?.account_name);
    if (!nameKey) {
      withoutName.push({ ...row, _dedupeKey: `__noname_${withoutName.length}` });
      continue;
    }
    const prev = byName.get(nameKey);
    if (!prev) {
      byName.set(nameKey, { ...row, _dedupeKey: nameKey });
      continue;
    }
    const pickDisplayName = (x, y) => {
      const sx = String(x || '').trim();
      const sy = String(y || '').trim();
      if (sy.length > sx.length) return sy;
      return sx || sy;
    };
    const summedOpens =
      Number(prev.total_human_opens || 0) + Number(row.total_human_opens || 0);
    const summedPersonas =
      Number(prev.personas_sent || 0) + Number(row.personas_sent || 0);
    byName.set(nameKey, {
      ...prev,
      account_name: pickDisplayName(prev.account_name, row.account_name),
      account_id: String(prev?.account_id || '').trim() || String(row?.account_id || '').trim(),
      personas_sent: summedPersonas,
      human_openers: Number(prev.human_openers || 0) + Number(row.human_openers || 0),
      human_clickers: Number(prev.human_clickers || 0) + Number(row.human_clickers || 0),
      total_human_opens: summedOpens,
      total_human_clicks:
        Number(prev.total_human_clicks || 0) + Number(row.total_human_clicks || 0),
      opens_to_personas_ratio:
        summedPersonas > 0
          ? Math.round((summedOpens / summedPersonas) * 100) / 100
          : 0,
      account_fit_score: mergeFit(prev.account_fit_score, row.account_fit_score),
      country: String(prev.country || '').trim() || String(row.country || '').trim() || prev.country,
    });
  }

  return [...byName.values(), ...withoutName];
}

const UNMAPPED_NOTE =
  'These messages were sent before per-message archetype tracking shipped (May 8, 2026). ' +
  'For historical messages we resolve archetype from the persona\'s current archetype if available; ' +
  'rows where the persona record doesn\'t have an archetype either fall here.';

function formatAvgTouches(val, personaCount) {
  const pc = Number(personaCount ?? 0);
  if (pc === 0) return '—';
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(1);
}

/** Best-rate cells only highlight when the row has enough volume to be meaningful. */
const MIN_HIGHLIGHT_SENDS = 20;

function rowMeetsHighlightVolume(row) {
  return Number(row?.total_sent ?? 0) >= MIN_HIGHLIGHT_SENDS;
}

function bestRateAmongEligible(rows, rateKey) {
  const eligible = (Array.isArray(rows) ? rows : []).filter(rowMeetsHighlightVolume);
  if (!eligible.length) return null;
  return Math.max(0, ...eligible.map((r) => Number(r?.[rateKey] || 0)));
}

function shouldHighlightRate(row, rate, bestRate) {
  if (!rowMeetsHighlightVolume(row)) return false;
  const r = Number(rate || 0);
  if (r <= 0 || bestRate == null) return false;
  return r === bestRate;
}

function avgTouchesSortValue(row) {
  const pc = Number(row?.persona_count || 0);
  if (pc === 0) return null;
  const n = Number(row?.avg_touches_per_persona);
  return Number.isFinite(n) ? n : null;
}

/** Null / missing numeric values sort last in both directions. */
function compareNullableNumber(av, bv, direction) {
  const aNull = av == null || !Number.isFinite(av);
  const bNull = bv == null || !Number.isFinite(bv);
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  const mul = direction === 'asc' ? 1 : -1;
  if (av < bv) return -1 * mul;
  if (av > bv) return 1 * mul;
  return 0;
}

// Global target-product filter — one switch drives every table on the tab.
const TARGET_PRODUCT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'SYS', label: 'SYS' },
  { value: 'EXAM', label: 'EXAM' },
  { value: 'ALL', label: 'ALL (multi)' },
];

function TopRoleTitlesLabel({ name, topRoleTitles, fallback = '—' }) {
  const roles = Array.isArray(topRoleTitles)
    ? topRoleTitles.filter((entry) => entry?.role_title)
    : [];
  const label = name || fallback;

  if (!roles.length) return label;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help border-b border-dashed border-gray-300">
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          <p className="text-xs font-semibold mb-1.5">Top roles in this bucket</p>
          <ul className="text-xs space-y-0.5">
            {roles.slice(0, 10).map(({ role_title, persona_count }) => (
              <li key={role_title}>
                {role_title} ({Number(persona_count || 0).toLocaleString()})
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TargetProductSwitch({ value, onChange }) {
  return (
    <div
      className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 self-start"
      role="group"
      aria-label="Target product filter"
    >
      {TARGET_PRODUCT_OPTIONS.map(({ value: optionValue, label }) => (
        <button
          key={optionValue || 'all'}
          type="button"
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === optionValue
              ? 'bg-white text-[#0A1A2F] shadow-sm'
              : 'text-gray-600 hover:text-[#0A1A2F]'
          }`}
          onClick={() => onChange(optionValue)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function PerformanceTable({ title, rows, keyField, labelField, labelFallback = '—', dimUnmapped = false, showAvgTouches = false, showTopRoleTitles = false }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ key: null, direction: 'desc' });
  const bestOpenRate = bestRateAmongEligible(rows, 'open_rate');
  const bestClickRate = bestRateAmongEligible(rows, 'click_rate');
  const bestReplyRate = bestRateAmongEligible(rows, 'reply_rate');

  const togglePerfSort = useCallback((key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const renderPerfSortArrow = (key) => {
    if (!showAvgTouches) return null;
    const effectiveKey = sort.key ?? 'total_sent';
    if (effectiveKey !== key) return null;
    const direction = sort.key == null && key === 'total_sent' ? 'desc' : sort.direction;
    return direction === 'asc'
      ? <ArrowUp className="w-3 h-3" />
      : <ArrowDown className="w-3 h-3" />;
  };

  const sortedRows = useMemo(() => {
    if (!showAvgTouches) return rows;
    const list = [...rows];
    const key = sort.key ?? 'total_sent';
    const direction = sort.key == null && key === 'total_sent' ? 'desc' : sort.direction;
    list.sort((a, b) => {
      if (key === keyField) {
        const av = String(a?.[keyField] || '').toLowerCase();
        const bv = String(b?.[keyField] || '').toLowerCase();
        const mul = direction === 'asc' ? 1 : -1;
        if (av < bv) return -1 * mul;
        if (av > bv) return 1 * mul;
        return 0;
      }
      if (key === 'total_sent') {
        return compareNullableNumber(Number(a?.total_sent || 0), Number(b?.total_sent || 0), direction);
      }
      if (key === 'avg_touches_per_persona') {
        return compareNullableNumber(avgTouchesSortValue(a), avgTouchesSortValue(b), direction);
      }
      return 0;
    });
    return list;
  }, [rows, sort, showAvgTouches, keyField]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-sm text-gray-500 py-4">{t('analyticsNoDataInPeriod')}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {showAvgTouches ? (
                    <button
                      type="button"
                      onClick={() => togglePerfSort(keyField)}
                      className="inline-flex items-center gap-1 font-medium hover:text-[#00A3A3]"
                    >
                      {labelField} {renderPerfSortArrow(keyField)}
                    </button>
                  ) : (
                    labelField
                  )}
                </TableHead>
                <TableHead className="text-right">
                  {showAvgTouches ? (
                    <button
                      type="button"
                      onClick={() => togglePerfSort('total_sent')}
                      className="inline-flex flex-col items-end gap-0.5 w-full font-medium hover:text-[#00A3A3]"
                    >
                      <span className="inline-flex items-center gap-1">
                        {t('analyticsTableSent')} {renderPerfSortArrow('total_sent')}
                      </span>
                      <span className="text-[10px] font-normal text-gray-400">personas</span>
                    </button>
                  ) : (
                    <>
                      <div>{t('analyticsTableSent')}</div>
                    </>
                  )}
                </TableHead>
                {showAvgTouches && (
                  <TableHead className="text-right">
                    <button
                      type="button"
                      onClick={() => togglePerfSort('avg_touches_per_persona')}
                      className="inline-flex items-center gap-1 justify-end w-full font-medium hover:text-[#00A3A3]"
                    >
                      Avg touches&nbsp;/&nbsp;persona {renderPerfSortArrow('avg_touches_per_persona')}
                    </button>
                  </TableHead>
                )}
                <TableHead className="text-right">{t('analyticsOpenRateCol')}</TableHead>
                <TableHead className="text-right">{t('analyticsClickRateCol')}</TableHead>
                <TableHead className="text-right">{t('analyticsReplyRateCol')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row) => {
                const rowKey = row?.[keyField] || row?.[labelField] || JSON.stringify(row);
                const isUnmapped = String(row?.[keyField] || '').toLowerCase() === '(unmapped)';
                const labelCell = (
                  <TableCell className="font-medium">
                    {isUnmapped && dimUnmapped ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="cursor-help border-b border-dashed border-gray-400"
                              title={UNMAPPED_NOTE}
                            >
                              {row?.[keyField] || labelFallback}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">{UNMAPPED_NOTE}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : showTopRoleTitles ? (
                      <TopRoleTitlesLabel
                        name={row?.[keyField]}
                        topRoleTitles={row?.top_role_titles}
                        fallback={labelFallback}
                      />
                    ) : (
                      row?.[keyField] || labelFallback
                    )}
                  </TableCell>
                );
                return (
                  <TableRow key={rowKey} className={dimUnmapped && isUnmapped ? 'opacity-60' : ''}>
                    {labelCell}
                    <TableCell className="text-right">
                      <div>{Number(row?.total_sent || 0)}</div>
                      {showAvgTouches && (
                        <div className="text-[10px] text-gray-400">
                          {Number(row?.persona_count || 0) > 0
                            ? `${Number(row.persona_count).toLocaleString()} personas`
                            : ''}
                        </div>
                      )}
                    </TableCell>
                    {showAvgTouches && (
                      <TableCell className="text-right font-medium">
                        {formatAvgTouches(row?.avg_touches_per_persona, row?.persona_count)}
                      </TableCell>
                    )}
                    <TableCell className={`text-right text-xs ${shouldHighlightRate(row, row?.open_rate, bestOpenRate) ? 'bg-emerald-50' : ''}`}>
                      <div className={shouldHighlightRate(row, row?.open_rate, bestOpenRate) ? 'font-semibold' : ''}>
                        {formatRate(row?.open_rate, row?.total_sent)}
                      </div>
                      {Number(row?.total_sent || 0) > 0 && (
                        <div className="text-gray-500">{Number(row?.open_rate_gross || 0).toFixed(1)}% gross</div>
                      )}
                    </TableCell>
                    <TableCell className={`text-right text-xs ${shouldHighlightRate(row, row?.click_rate, bestClickRate) ? 'bg-emerald-50' : ''}`}>
                      <div className={shouldHighlightRate(row, row?.click_rate, bestClickRate) ? 'font-semibold' : ''}>
                        {formatRate(row?.click_rate, row?.total_sent)}
                      </div>
                      {Number(row?.total_sent || 0) > 0 && (
                        <div className="text-gray-500">{Number(row?.click_rate_gross || 0).toFixed(1)}% gross</div>
                      )}
                    </TableCell>
                    <TableCell className={`text-right ${shouldHighlightRate(row, row?.reply_rate, bestReplyRate) ? 'bg-emerald-50' : ''}`}>
                      <Badge variant="secondary">
                        {formatRate(row?.reply_rate, row?.total_sent)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function MostActiveTab({ tenantId, timeRange, analyticsWindow, userId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [topAccounts, setTopAccounts] = useState([]);
  const [playbookPerf, setPlaybookPerf] = useState([]);
  const [adHocPerf, setAdHocPerf] = useState(null);
  const [archetypePerf, setArchetypePerf] = useState([]);
  const [departmentPerf, setDepartmentPerf] = useState([]);
  const [sectorPerf, setSectorPerf] = useState([]);
  const [countryHeatmapCells, setCountryHeatmapCells] = useState([]);
  const [countryHeatmapError, setCountryHeatmapError] = useState(false);
  const [topSort, setTopSort] = useState({ key: 'opens_to_personas_ratio', direction: 'desc' });
  const [countryFilter, setCountryFilter] = useState(new Set()); // empty = all countries
  const [targetProductFilter, setTargetProductFilter] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          tenant_id: tenantId,
          days: String(timeRange || 30),
        });
        appendAnalyticsWindow(params, coerceAnalyticsWindow(analyticsWindow ?? timeRange));
        if (userId) params.append('user_id', userId);
        // BE: scope aggregates to accounts/personas with is_visible !== false (same as Overview distribution).
        params.append('only_visible_accounts', 'true');
        params.append('only_visible_personas', 'true');
        const qs = params.toString();

        const acctQs = (f) => f ? `${qs}&target_products=${encodeURIComponent(f)}` : qs;
        const productQs = acctQs(targetProductFilter);

        const [topRes, pbRes, adhocRes, archRes, sectorRes, heatmapRes, deptRes, accountsRows] = await Promise.all([
          fetch(`${API_BASE_URL}/api/email-analytics/top-accounts?${productQs}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/by-playbook?${productQs}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/ad-hoc?${productQs}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/by-archetype?${productQs}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/by-sector?${productQs}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/country-heatmap?${productQs}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/by-department?${productQs}`, { credentials: 'include' }),
          AccountFinal.list().catch(() => []),
        ]);

        if (cancelled) return;

        const topData = topRes.ok ? await topRes.json() : [];
        const pbData = pbRes.ok ? await pbRes.json() : [];
        const adhocData = adhocRes.ok ? await adhocRes.json() : null;
        const archData = archRes.ok ? await archRes.json() : [];
        const sectorData = sectorRes.ok ? await sectorRes.json() : [];
        const deptData = deptRes.ok ? await deptRes.json() : [];

        let heatmapCells = [];
        let heatmapErr = false;
        if (!heatmapRes.ok) {
          heatmapErr = true;
        } else {
          try {
            const heatmapJson = await heatmapRes.json();
            heatmapCells = Array.isArray(heatmapJson?.cells) ? heatmapJson.cells : [];
          } catch {
            heatmapErr = true;
          }
        }

        const fitByAccountId = new Map();
        const fitByAccountName = new Map();
        const countryByAccountId = new Map();
        const countryByAccountName = new Map();
        const hiddenAccountIds = new Set();
        const hiddenAccountNames = new Set();
        (Array.isArray(accountsRows) ? accountsRows : []).forEach((acc) => {
          if (acc?.tenant_id !== tenantId && acc?.tenant_id !== 'global') return;
          const accountId = String(acc?.account_id || acc?.id || '').trim();
          const accountName = String(acc?.account_name || '').trim().toLowerCase();
          if (acc?.is_visible === false) {
            if (accountId) hiddenAccountIds.add(accountId);
            if (accountName) hiddenAccountNames.add(accountName);
            return;
          }
          const country = String(acc?.country || '').trim();
          const countryNorm = country.toLowerCase();
          const isCountryUnknown =
            !country || ['unknown', 'n/a', 'na', '-'].includes(countryNorm);
          const fitRaw = acc?.account_fit_score ?? acc?.account_fit ?? acc?.fit_score ?? null;
          const fit = fitRaw === null || fitRaw === undefined || fitRaw === '' ? null : Number(fitRaw);
          if (!Number.isFinite(fit)) return;
          if (accountId && !fitByAccountId.has(accountId)) fitByAccountId.set(accountId, fit);
          if (accountName && !fitByAccountName.has(accountName)) fitByAccountName.set(accountName, fit);
          if (!isCountryUnknown) {
            if (accountId && !countryByAccountId.has(accountId)) countryByAccountId.set(accountId, country);
            if (accountName && !countryByAccountName.has(accountName)) countryByAccountName.set(accountName, country);
          }
        });

        const topWithFit = (Array.isArray(topData) ? topData : [])
          .map((row) => {
            const accountId = String(row?.account_id || '').trim();
            const accountName = String(row?.account_name || '').trim().toLowerCase();
            const fit = fitByAccountId.get(accountId) ?? fitByAccountName.get(accountName) ?? null;
            const country = (() => {
              const fromMap =
                countryByAccountId.get(accountId) ?? countryByAccountName.get(accountName) ?? null;
              if (!fromMap) return null;
              const n = String(fromMap).trim().toLowerCase();
              return ['unknown', 'n/a', 'na', '-'].includes(n) ? null : fromMap;
            })();
            return { ...row, account_fit_score: fit, country };
          })
          .filter((row) => {
            const accountId = String(row?.account_id || '').trim();
            const accountName = String(row?.account_name || '').trim().toLowerCase();
            if (accountId && hiddenAccountIds.has(accountId)) return false;
            if (accountName && hiddenAccountNames.has(accountName)) return false;
            return true;
          });

        setTopAccounts(mergeTopAccountsRows(topWithFit));
        setPlaybookPerf(Array.isArray(pbData) ? pbData : []);
        setAdHocPerf(adhocData && typeof adhocData === 'object' ? adhocData : null);
        setArchetypePerf(Array.isArray(archData) ? archData : []);
        setDepartmentPerf(Array.isArray(deptData) ? deptData : []);
        setSectorPerf(Array.isArray(sectorData) ? sectorData : []);
        setCountryHeatmapCells(heatmapCells);
        setCountryHeatmapError(heatmapErr);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading Most Active analytics:', error);
          setTopAccounts([]);
          setPlaybookPerf([]);
          setAdHocPerf(null);
          setArchetypePerf([]);
          setDepartmentPerf([]);
          setSectorPerf([]);
          setCountryHeatmapCells([]);
          setCountryHeatmapError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [tenantId, timeRange, analyticsWindow, userId, targetProductFilter]);

  const adHocRow = useMemo(() => (adHocPerf ? [adHocPerf] : []), [adHocPerf]);
  const adHocHighlight = rowMeetsHighlightVolume(adHocPerf);
  const availableCountries = useMemo(() => {
    const countries = new Set();
    topAccounts.forEach((row) => {
      const country = String(row?.country || '').trim();
      if (country) countries.add(country);
    });
    return Array.from(countries).sort((a, b) => a.localeCompare(b));
  }, [topAccounts]);

  const filteredTopAccounts = useMemo(() => {
    if (countryFilter.size === 0) return topAccounts;
    return topAccounts.filter((row) => {
      const country = String(row?.country || '').trim();
      return country && countryFilter.has(country);
    });
  }, [topAccounts, countryFilter]);

  const sortedTopAccounts = useMemo(() => {
    const rows = [...filteredTopAccounts];
    const { key, direction } = topSort;
    rows.sort((a, b) => {
      let av;
      let bv;
      if (key === 'account_name') {
        av = String(a?.account_name || '').toLowerCase();
        bv = String(b?.account_name || '').toLowerCase();
      } else {
        av = Number(a?.[key] || 0);
        bv = Number(b?.[key] || 0);
      }
      if (av < bv) return direction === 'asc' ? -1 : 1;
      if (av > bv) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filteredTopAccounts, topSort]);

  const toggleTopSort = (key) => {
    setTopSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const renderTopSortArrow = (key) => {
    if (topSort.key !== key) return null;
    return topSort.direction === 'asc'
      ? <ArrowUp className="w-3 h-3" />
      : <ArrowDown className="w-3 h-3" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-[#0A1A2F]">Target product</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Applies to every table on this tab — heatmap, sector, archetype, accounts, playbooks, and ad-hoc.
          </p>
        </div>
        <TargetProductSwitch value={targetProductFilter} onChange={setTargetProductFilter} />
      </div>

      <CountryActivityHeatmapCard
        cells={countryHeatmapCells.filter(
          (c) => String(c?.country || '').toLowerCase() !== '(unknown)'
        )}
        loadError={countryHeatmapError}
      />

      <PerformanceTable
        title={t('performanceBySector')}
        rows={sectorPerf.filter(
          (r) => String(r?.sector_name || '').toLowerCase() !== '(unmapped)'
        )}
        keyField="sector_name"
        labelField={t('sector')}
        labelFallback="—"
        showAvgTouches
      />

      <PerformanceTable
        title={t('performanceByArchetype')}
        rows={archetypePerf.filter(
          (r) => String(r?.archetype_name || '').toLowerCase() !== '(unmapped)'
        )}
        keyField="archetype_name"
        labelField={t('archetype')}
        labelFallback="—"
        showAvgTouches
        showTopRoleTitles
      />

      <PerformanceTable
        title={t('performanceByDepartment')}
        rows={departmentPerf.filter(
          (r) => String(r?.department_name || '').toLowerCase() !== '(unmapped)'
        )}
        keyField="department_name"
        labelField={t('department')}
        labelFallback="—"
        showAvgTouches
        showTopRoleTitles
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {t('mostActiveAccounts')}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">{t('mostActiveAccountsTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full md:w-[190px] text-sm justify-start min-w-0">
                  <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                    {countryFilter.size === 0
                      ? 'All Countries'
                      : countryFilter.size === 1
                        ? Array.from(countryFilter)[0]
                        : `${countryFilter.size} Selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-1" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] max-h-[320px] overflow-y-auto p-2" align="start">
                <div className="space-y-2">
                  <div className="font-semibold text-sm mb-2 px-2">Select Countries</div>
                  {availableCountries.map((country) => {
                    const isSelected = countryFilter.has(country);
                    return (
                      <label
                        key={country}
                        className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            setCountryFilter((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(country);
                              else next.delete(country);
                              return next;
                            });
                          }}
                        />
                        <span className="text-sm cursor-pointer flex-1">{country}</span>
                      </label>
                    );
                  })}
                  {countryFilter.size > 0 && (
                    <div className="border-t pt-2 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => setCountryFilter(new Set())}
                      >
                        {t('clearAll')}
                      </Button>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {sortedTopAccounts.length === 0 ? (
            <div className="text-sm text-gray-500 py-4">{t('mostActiveAccountsEmpty')}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button type="button" onClick={() => toggleTopSort('account_name')} className="inline-flex items-center gap-1">
                      {t('analyticsAccount')} {renderTopSortArrow('account_name')}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" onClick={() => toggleTopSort('account_fit_score')} className="inline-flex items-center gap-1">
                      {t('analyticsAccountFit')} {renderTopSortArrow('account_fit_score')}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" onClick={() => toggleTopSort('personas_sent')} className="inline-flex items-center gap-1">
                      {t('analyticsPersonasSent')} {renderTopSortArrow('personas_sent')}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" onClick={() => toggleTopSort('human_openers')} className="inline-flex items-center gap-1">
                      {t('analyticsMultiPersonaOpens')} {renderTopSortArrow('human_openers')}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button
                      type="button"
                      onClick={() => toggleTopSort('opens_to_personas_ratio')}
                      className="inline-flex items-center gap-1 justify-end w-full"
                    >
                      <span className="inline-flex flex-col items-end gap-0.5">
                        <span className="inline-flex items-center gap-1">
                          {t('analyticsOpensPerPersona')} {renderTopSortArrow('opens_to_personas_ratio')}
                        </span>
                        <span className="text-[10px] font-normal text-gray-400">
                          {t('analyticsOpensPerPersonaSubtitle')}
                        </span>
                      </span>
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" onClick={() => toggleTopSort('human_clickers')} className="inline-flex items-center gap-1">
                      {t('analyticsMultiPersonaClicks')} {renderTopSortArrow('human_clickers')}
                    </button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTopAccounts.map((a) => {
                  const accountHref = getAccountFinalPageUrl(a.account_id);
                  const accountLabel = a.account_name || '—';
                  return (
                  <TableRow key={a._dedupeKey || a.account_id || a.account_name}>
                    <TableCell className="font-medium max-w-[240px]">
                      <div className="truncate">
                        {accountHref ? (
                          <Link
                            to={accountHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0A1A2F] hover:text-[#00A3A3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                          >
                            {accountLabel}
                          </Link>
                        ) : (
                          <span>{accountLabel}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatAccountFitInteger(a.account_fit_score)}</TableCell>
                    <TableCell className="text-right">{Number(a.personas_sent || 0)}</TableCell>
                    <TableCell className="text-right">{Number(a.human_openers || 0)}</TableCell>
                    <TableCell className="text-right">
                      <div className="font-semibold">
                        {Number(a.opens_to_personas_ratio || 0).toFixed(2)}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {Number(a.total_human_opens || 0)} opens · {Number(a.personas_sent || 0)} personas
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{Number(a.human_clickers || 0)}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PerformanceTable
        title={t('playbookPerformance')}
        rows={playbookPerf}
        keyField="playbook_key"
        labelField={t('playbook')}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {t('adHocMessagePerformance')}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">{t('adHocReplyRateTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {adHocRow.length === 0 ? (
            <div className="text-sm text-gray-500 py-2">{t('analyticsNoDataInPeriod')}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('analyticsChannelCol')}</TableHead>
                  <TableHead className="text-right">{t('analyticsTableSent')}</TableHead>
                  <TableHead className="text-right">{t('analyticsOpenRateCol')}</TableHead>
                  <TableHead className="text-right">{t('analyticsClickRateCol')}</TableHead>
                  <TableHead className="text-right">{t('analyticsReplyRateCol')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">{t('adHoc')}</TableCell>
                  <TableCell className="text-right">{Number(adHocPerf?.total_sent || 0)}</TableCell>
                  <TableCell className={`text-right text-xs ${adHocHighlight ? 'bg-emerald-50' : ''}`}>
                    <div className={adHocHighlight ? 'font-semibold' : ''}>{formatRate(adHocPerf?.open_rate, adHocPerf?.total_sent)}</div>
                    {Number(adHocPerf?.total_sent || 0) > 0 && (
                      <div className="text-gray-500">{Number(adHocPerf?.open_rate_gross || 0).toFixed(1)}% gross</div>
                    )}
                  </TableCell>
                  <TableCell className={`text-right text-xs ${adHocHighlight ? 'bg-emerald-50' : ''}`}>
                    <div className={adHocHighlight ? 'font-semibold' : ''}>{formatRate(adHocPerf?.click_rate, adHocPerf?.total_sent)}</div>
                    {Number(adHocPerf?.total_sent || 0) > 0 && (
                      <div className="text-gray-500">{Number(adHocPerf?.click_rate_gross || 0).toFixed(1)}% gross</div>
                    )}
                  </TableCell>
                  <TableCell className={`text-right ${adHocHighlight ? 'bg-emerald-50' : ''}`}>
                    <Badge variant="secondary">
                      {formatRate(adHocPerf?.reply_rate, adHocPerf?.total_sent)}
                    </Badge>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
